import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, Image as ImageIcon, X, Mail, Upload, Layers, AlertTriangle } from 'lucide-react';

function normalizeTitle(title) {
  return (title || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function filenameToTitle(filename) {
  return filename
    .replace(/\.[^/.]+$/, '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function ProductCard({ product, selected, onToggleSelect, onEdit, onDelete }) {
  return (
    <div
      className="product-card"
      style={{
        background: '#fff',
        border: selected ? '2px solid #4f46e5' : '1px solid #e0e0e0',
        borderRadius: '12px',
        overflow: 'hidden',
        opacity: product.active ? 1 : 0.6,
        position: 'relative'
      }}
    >
      <label
        onClick={e => e.stopPropagation()}
        style={{
          position: 'absolute', top: '10px', left: '10px', zIndex: 1,
          background: 'rgba(255,255,255,0.9)', borderRadius: '6px', padding: '4px',
          display: 'flex', cursor: 'pointer'
        }}
      >
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect(product.id)}
          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
        />
      </label>
      <div style={{ aspectRatio: '7/5', overflow: 'hidden', background: '#f5f5f5' }}>
        <img
          src={product.image_url}
          alt={product.title}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>
      <div style={{ padding: '15px' }}>
        <h3 style={{ fontSize: '15px', margin: '0 0 5px', fontWeight: '600' }}>{product.title}</h3>
        {product.category && (
          <p style={{ fontSize: '12px', color: '#4f46e5', margin: '0 0 4px', fontWeight: '500' }}>
            {product.category}{product.subcategory ? ` — ${product.subcategory}` : ''}
          </p>
        )}
        <p style={{ fontSize: '13px', color: '#666', margin: '0 0 5px' }}>
          $6 / $30 / $40
        </p>
        <p style={{ fontSize: '13px', color: product.stock === 0 ? '#dc2626' : '#666', margin: '0 0 10px' }}>
          {product.stock === null || product.stock === undefined ? 'Stock: Unlimited' : product.stock === 0 ? 'Out of Stock' : `Stock: ${product.stock}`}
        </p>
        {!product.active && (
          <span style={{
            background: '#fee2e2',
            color: '#dc2626',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            marginBottom: '10px',
            display: 'inline-block'
          }}>
            Inactive
          </span>
        )}
        <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(product)}
            style={{ flex: 1 }}
          >
            <Edit className="w-4 h-4 mr-1" />
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(product.id)}
            style={{ color: '#dc2626', borderColor: '#dc2626' }}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

const NOTECARD_CATEGORIES = [
  'Beachscapes',
  'Best Selling',
  'Digital Art',
  'Dodging Shadows',
  'Flora & Fauna',
  'Landmarks',
  'OBX Sunrise',
  'Pelicans',
  'Ripples',
  'Waves',
];

function AdminNotecards({ onLogout }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedSubcategories, setExpandedSubcategories] = useState({});

  // Batch upload state
  const [batchMode, setBatchMode] = useState(false);
  const [batchItems, setBatchItems] = useState([]);
  const [batchUploading, setBatchUploading] = useState(false);
  const [batchSaving, setBatchSaving] = useState(false);

  // Bulk selection state (checkboxes on the grid below)
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkWorking, setBulkWorking] = useState(false);
  const [bulkCategoryChoice, setBulkCategoryChoice] = useState('');
  const [bulkSubcategoryChoice, setBulkSubcategoryChoice] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    active: true,
    stock: null,
    category: '',
    subcategory: ''
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get('/admin/notecards-products');
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to fetch notecards');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.image_url) {
      toast.error('Please upload an image');
      return;
    }
    
    try {
      if (editingProduct) {
        await axios.put(`/admin/notecards-products/${editingProduct.id}`, formData);
        toast.success('Notecard updated successfully');
      } else {
        await axios.post('/admin/notecards-products', formData);
        toast.success('Notecard added successfully');
      }
      setShowDialog(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error('Failed to save notecard');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      image_url: '',
      active: true,
      stock: null,
      category: '',
      subcategory: ''
    });
    setEditingProduct(null);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title,
      description: product.description || '',
      image_url: product.image_url,
      active: product.active !== false,
      stock: product.stock ?? null,
      category: product.category || '',
      subcategory: product.subcategory || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this notecard?')) return;
    
    try {
      await axios.delete(`/admin/notecards-products/${productId}`);
      toast.success('Notecard deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete notecard');
    }
  };

  const groupedProducts = () => {
    const groups = {};
    products.forEach(p => {
      const cat = p.category || 'Uncategorized';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(p);
    });
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      const ia = NOTECARD_CATEGORIES.indexOf(a);
      const ib = NOTECARD_CATEGORIES.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
    const sorted = {};
    sortedKeys.forEach(k => { sorted[k] = groups[k]; });
    return sorted;
  };

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const jumpToCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: true }));
    setTimeout(() => {
      document.getElementById(`admin-notecard-cat-${cat}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const existingSubcategories = [...new Set(products.map(p => p.subcategory).filter(Boolean))].sort();

  // Any category can carry a subcategory, but in practice only Digital Art
  // does — group by subcategory only when a category actually has one set,
  // so Pelicans/Waves/etc. render as a flat grid same as before.
  const groupBySubcategory = (items) => {
    if (!items.some(i => i.subcategory)) return null;
    const groups = {};
    items.forEach(i => {
      const sub = i.subcategory || 'Unsorted';
      if (!groups[sub]) groups[sub] = [];
      groups[sub].push(i);
    });
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === 'Unsorted') return 1;
      if (b === 'Unsorted') return -1;
      return a.localeCompare(b);
    });
    const sorted = {};
    sortedKeys.forEach(k => { sorted[k] = groups[k]; });
    return sorted;
  };

  const toggleSubcategory = (key) => {
    setExpandedSubcategories(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // ── Bulk selection ──────────────────────────────────────────────────────

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectGroup = (items) => {
    const ids = items.map(i => i.id);
    const allSelected = ids.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds.size} selected notecard(s)? This cannot be undone.`)) return;
    setBulkWorking(true);
    try {
      const resp = await axios.post('/admin/notecards-products/bulk-delete', { ids: [...selectedIds] });
      toast.success(resp.data.message);
      clearSelection();
      fetchProducts();
    } catch {
      toast.error('Bulk delete failed');
    } finally {
      setBulkWorking(false);
    }
  };

  const handleBulkUpdate = async (updates, successMessage) => {
    setBulkWorking(true);
    try {
      const resp = await axios.post('/admin/notecards-products/bulk-update', { ids: [...selectedIds], ...updates });
      toast.success(successMessage || resp.data.message);
      clearSelection();
      setBulkCategoryChoice('');
      setBulkSubcategoryChoice('');
      fetchProducts();
    } catch {
      toast.error('Bulk update failed');
    } finally {
      setBulkWorking(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Maximum size: 10MB');
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post('/upload/image', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.url) {
        setFormData({ ...formData, image_url: response.data.url });
        toast.success('Image uploaded!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Image upload failed');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  // Flags each batch item whose normalized title matches an already-saved
  // notecard, or another item in this same batch — catches the common case
  // of re-selecting overlapping files (or the same folder twice) without
  // needing to compare image bytes.
  const flagDuplicates = (items) => {
    const existingTitles = new Set(products.map(p => normalizeTitle(p.title)));
    const seen = new Set();
    return items.map(item => {
      const norm = normalizeTitle(item.title);
      let duplicateWarning = null;
      if (norm) {
        if (existingTitles.has(norm)) duplicateWarning = 'Matches an existing notecard';
        else if (seen.has(norm)) duplicateWarning = 'Duplicate title within this batch';
      }
      seen.add(norm);
      return { ...item, duplicateWarning };
    });
  };

  const handleBatchFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setBatchUploading(true);
    const formDataUpload = new FormData();
    files.forEach(f => formDataUpload.append('files', f));

    try {
      const response = await axios.post('/upload/multiple', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const newItems = response.data.uploaded.map(({ filename, url }) => ({
        _id: crypto.randomUUID(),
        url,
        title: filenameToTitle(filename),
        category: ''
      }));

      setBatchItems(prev => flagDuplicates([...prev, ...newItems]));

      const dupCount = newItems.filter(i => normalizeTitle(i.title) &&
        (products.some(p => normalizeTitle(p.title) === normalizeTitle(i.title)))).length;

      if (response.data.errors?.length) {
        toast.error(`${response.data.errors.length} file(s) failed to upload`);
      } else if (dupCount > 0) {
        toast.warning(`${newItems.length} notecard(s) uploaded — ${dupCount} look like possible duplicates, check the warnings below`);
      } else {
        toast.success(`${newItems.length} notecard(s) uploaded`);
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setBatchUploading(false);
      e.target.value = '';
    }
  };

  const updateBatchItem = (id, field, value) => {
    setBatchItems(prev => {
      const updated = prev.map(item => item._id === id ? { ...item, [field]: value } : item);
      return field === 'title' ? flagDuplicates(updated) : updated;
    });
  };

  const removeBatchItem = (id) => {
    setBatchItems(prev => prev.filter(item => item._id !== id));
  };

  const handleBatchSave = async () => {
    const untitled = batchItems.filter(item => !item.title.trim());
    if (untitled.length) {
      toast.error('Please add a title to every notecard before saving');
      return;
    }

    const flaggedCount = batchItems.filter(item => item.duplicateWarning).length;
    if (flaggedCount > 0 && !window.confirm(
      `${flaggedCount} of these look like possible duplicates. Save all ${batchItems.length} anyway? ` +
      `(Remove the flagged ones with the × button first if you don't want them.)`
    )) {
      return;
    }

    setBatchSaving(true);
    let saved = 0;
    let failed = 0;

    for (const item of batchItems) {
      try {
        await axios.post('/admin/notecards-products', {
          title: item.title.trim(),
          description: '',
          image_url: item.url,
          category: item.category || '',
          subcategory: item.subcategory || '',
          active: false,
          stock: null
        });
        saved++;
      } catch {
        failed++;
      }
    }

    setBatchSaving(false);

    if (saved > 0) {
      toast.success(`${saved} notecard(s) saved as inactive — activate them from the grid`);
      setBatchItems([]);
      setBatchMode(false);
      fetchProducts();
    }
    if (failed > 0) {
      toast.error(`${failed} notecard(s) failed to save`);
    }
  };

  return (
    <AdminLayout onLogout={onLogout} currentPage="notecards">
      <div data-testid="notecards-admin-page">
        <datalist id="subcategory-options">
          {existingSubcategories.map(sub => <option key={sub} value={sub} />)}
        </datalist>
        <div className="page-header">
          <h1>Mostly Good Notecards</h1>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button
              variant="outline"
              onClick={() => setBatchMode(m => !m)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <Layers size={16} />
              {batchMode ? 'Cancel Batch' : 'Batch Upload'}
            </Button>
          <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-notecard-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Notecard
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingProduct ? 'Edit Notecard' : 'Add New Notecard'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                {/* Image Upload */}
                <div className="form-group">
                  <label>Notecard Design Image *</label>
                  {formData.image_url ? (
                    <div style={{ position: 'relative', display: 'inline-block', marginTop: '10px' }}>
                      <img 
                        src={formData.image_url} 
                        alt="Notecard preview" 
                        style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px' }} 
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, image_url: '' })}
                        style={{
                          position: 'absolute',
                          top: '-10px',
                          right: '-10px',
                          background: '#ef4444',
                          border: 'none',
                          borderRadius: '50%',
                          padding: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  ) : (
                    <div className="upload-area" style={{ 
                      border: '2px dashed #ddd', 
                      padding: '40px', 
                      textAlign: 'center', 
                      borderRadius: '8px',
                      marginTop: '10px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploading}
                        style={{ display: 'none' }}
                        id="image-upload"
                      />
                      <label htmlFor="image-upload" style={{ cursor: 'pointer' }}>
                        {uploading ? (
                          <div>Uploading...</div>
                        ) : (
                          <>
                            <ImageIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                            <p className="text-gray-500">Click to upload notecard design image</p>
                          </>
                        )}
                      </label>
                    </div>
                  )}
                </div>

                {/* Title */}
                <div className="form-group" style={{ marginTop: '20px' }}>
                  <label>Notecard Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    placeholder="e.g., Sunrise Over the Dunes"
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', marginTop: '5px' }}
                  />
                </div>

                {/* Description */}
                <div className="form-group" style={{ marginTop: '15px' }}>
                  <label>Description (optional)</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of the notecard design"
                    rows={3}
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', marginTop: '5px' }}
                  />
                </div>

                {/* Category */}
                <div className="form-group" style={{ marginTop: '15px' }}>
                  <label>Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', marginTop: '5px' }}
                  >
                    <option value="">-- Select Category --</option>
                    {NOTECARD_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Subcategory — only Digital Art needs this extra level */}
                {formData.category === 'Digital Art' && (
                  <div className="form-group" style={{ marginTop: '15px' }}>
                    <label>Subcategory <span style={{ color: '#999', fontWeight: 'normal' }}>(optional — e.g. Holiday, Wildlife, Coastal)</span></label>
                    <input
                      type="text"
                      list="subcategory-options"
                      value={formData.subcategory}
                      onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                      placeholder="e.g., Holiday"
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', marginTop: '5px' }}
                    />
                  </div>
                )}

                {/* Pricing Info */}
                <div style={{ marginTop: '15px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                  <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                    <strong>Standard Pricing:</strong><br />
                    Single: $6 • Six-Pak: $30 • Ten-Pak: $40<br />
                    <span style={{ fontSize: '12px' }}>5x7, blank inside, individually wrapped, envelope included</span>
                  </p>
                </div>

                {/* Stock */}
                <div className="form-group" style={{ marginTop: '15px' }}>
                  <label>Quantity in Stock</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.stock ?? ''}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                    placeholder="Leave blank for unlimited"
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', marginTop: '5px' }}
                  />
                </div>

                {/* Active Toggle */}
                <div className="form-group" style={{ marginTop: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      style={{ width: '18px', height: '18px' }}
                    />
                    Active (visible on shop page)
                  </label>
                </div>

                {/* Submit */}
                <div style={{ marginTop: '25px', display: 'flex', gap: '10px' }}>
                  <Button type="submit" style={{ flex: 1 }}>
                    {editingProduct ? 'Update Notecard' : 'Add Notecard'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* ===== BATCH UPLOAD PANEL ===== */}
        {batchMode && (
          <div style={{ marginTop: '24px' }}>
            <label style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              border: '2px dashed #aaa', borderRadius: '12px', padding: '40px 20px',
              cursor: batchUploading ? 'not-allowed' : 'pointer',
              background: batchUploading ? '#f5f5f5' : '#fafafa',
              marginBottom: '24px'
            }}>
              <Upload size={36} color="#888" style={{ marginBottom: '12px' }} />
              <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#444' }}>
                {batchUploading ? 'Uploading…' : 'Click to select notecard images'}
              </p>
              <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#888' }}>
                Select multiple files at once — they'll all upload together
              </p>
              <input
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handleBatchFileSelect}
                disabled={batchUploading}
              />
            </label>

            {batchItems.length > 0 && (
              <>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
                  {batchItems.length} notecard{batchItems.length !== 1 ? 's' : ''} ready — fill in details then save. All will be saved as <strong>inactive</strong> until you activate them.
                </p>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                  gap: '20px',
                  marginBottom: '24px'
                }}>
                  {batchItems.map(item => (
                    <div key={item._id} style={{
                      background: '#fff', borderRadius: '12px', overflow: 'hidden',
                      boxShadow: item.duplicateWarning ? '0 0 0 2px #f59e0b, 0 2px 8px rgba(0,0,0,0.1)' : '0 2px 8px rgba(0,0,0,0.1)',
                      position: 'relative'
                    }}>
                      <button
                        onClick={() => removeBatchItem(item._id)}
                        style={{
                          position: 'absolute', top: '8px', right: '8px', zIndex: 1,
                          background: 'rgba(0,0,0,0.65)', color: '#fff', border: 'none',
                          borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}
                      >
                        <X size={14} />
                      </button>
                      <img src={item.url} alt={item.title} style={{ width: '100%', height: '180px', objectFit: 'cover', display: 'block' }} />
                      {item.duplicateWarning && (
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          background: '#fffbeb', color: '#92400e', padding: '8px 12px',
                          fontSize: '12px', fontWeight: '600', borderBottom: '1px solid #fde68a'
                        }}>
                          <AlertTriangle size={13} /> {item.duplicateWarning}
                        </div>
                      )}
                      <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div>
                          <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>Title *</label>
                          <input
                            type="text"
                            value={item.title}
                            onChange={e => updateBatchItem(item._id, 'title', e.target.value)}
                            style={{ width: '100%', padding: '7px 10px', border: '1px solid #d0d0d0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>Category</label>
                          <select
                            value={item.category}
                            onChange={e => updateBatchItem(item._id, 'category', e.target.value)}
                            style={{ width: '100%', padding: '7px 10px', border: '1px solid #d0d0d0', borderRadius: '6px', fontSize: '13px', background: '#fff' }}
                          >
                            <option value="">— Select —</option>
                            {NOTECARD_CATEGORIES.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>
                        {item.category === 'Digital Art' && (
                          <div>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' }}>Subcategory</label>
                            <input
                              type="text"
                              list="subcategory-options"
                              value={item.subcategory || ''}
                              onChange={e => updateBatchItem(item._id, 'subcategory', e.target.value)}
                              placeholder="e.g., Holiday"
                              style={{ width: '100%', padding: '7px 10px', border: '1px solid #d0d0d0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <Button variant="outline" onClick={() => { setBatchItems([]); setBatchMode(false); }}>
                    Cancel
                  </Button>
                  <Button onClick={handleBatchSave} disabled={batchSaving} style={{ minWidth: '180px' }}>
                    {batchSaving ? 'Saving…' : `Save ${batchItems.length} Notecard${batchItems.length !== 1 ? 's' : ''}`}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Products List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
        ) : products.length === 0 ? (
          <div className="empty-state" style={{ textAlign: 'center', padding: '60px 20px', background: '#f9f9f9', borderRadius: '12px' }}>
            <Mail className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 style={{ margin: '0 0 10px' }}>No Notecards Yet</h3>
            <p style={{ color: '#666', margin: '0 0 20px' }}>Add your first notecard design to get started.</p>
            <Button onClick={() => setShowDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Notecard
            </Button>
          </div>
        ) : (
          <>
            {/* Bulk action toolbar — appears once anything is checked below */}
            {selectedIds.size > 0 && (
              <div style={{
                position: 'sticky', top: 0, zIndex: 5,
                display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px',
                background: '#1a1a1a', color: '#fff', borderRadius: '10px',
                padding: '12px 16px', margin: '20px 0'
              }}>
                <strong>{selectedIds.size} selected</strong>

                <Button variant="outline" size="sm" onClick={handleBulkDelete} disabled={bulkWorking}
                  style={{ background: '#fff', color: '#dc2626', borderColor: '#dc2626' }}>
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleBulkUpdate({ active: true }, 'Activated')} disabled={bulkWorking} style={{ background: '#fff' }}>
                  Activate
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleBulkUpdate({ active: false }, 'Deactivated')} disabled={bulkWorking} style={{ background: '#fff' }}>
                  Deactivate
                </Button>

                <select
                  value={bulkCategoryChoice}
                  onChange={e => setBulkCategoryChoice(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: '6px', border: 'none', fontSize: '13px' }}
                >
                  <option value="">Move to category…</option>
                  {NOTECARD_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
                <Button
                  variant="outline" size="sm" disabled={bulkWorking || !bulkCategoryChoice}
                  onClick={() => handleBulkUpdate({ category: bulkCategoryChoice }, `Moved to ${bulkCategoryChoice}`)}
                  style={{ background: '#fff' }}
                >
                  Apply
                </Button>

                <input
                  type="text"
                  list="subcategory-options"
                  value={bulkSubcategoryChoice}
                  onChange={e => setBulkSubcategoryChoice(e.target.value)}
                  placeholder="Set subcategory…"
                  style={{ padding: '6px 10px', borderRadius: '6px', border: 'none', fontSize: '13px', width: '160px' }}
                />
                <Button
                  variant="outline" size="sm" disabled={bulkWorking || !bulkSubcategoryChoice.trim()}
                  onClick={() => handleBulkUpdate({ subcategory: bulkSubcategoryChoice.trim() }, `Subcategory set to ${bulkSubcategoryChoice.trim()}`)}
                  style={{ background: '#fff' }}
                >
                  Apply
                </Button>

                <button
                  onClick={clearSelection}
                  style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#bbb', cursor: 'pointer', fontSize: '13px', textDecoration: 'underline' }}
                >
                  Clear selection
                </button>
              </div>
            )}

            {/* Jump-to-category nav */}
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: '10px',
              marginTop: '20px', marginBottom: '20px'
            }}>
              {Object.keys(groupedProducts()).map(cat => (
                <button
                  key={cat}
                  onClick={() => jumpToCategory(cat)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '20px',
                    border: '2px solid #ddd',
                    background: '#fff',
                    color: '#444',
                    fontFamily: 'inherit',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>

            {Object.entries(groupedProducts()).map(([category, items]) => {
              const expanded = !!expandedCategories[category];
              const subGroups = groupBySubcategory(items);
              const allInCategorySelected = items.every(i => selectedIds.has(i.id));
              return (
                <div
                  key={category}
                  id={`admin-notecard-cat-${category}`}
                  style={{ border: '1px solid #e0e0e0', borderRadius: '12px', overflow: 'hidden', marginBottom: '16px' }}
                >
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '14px 18px', background: '#f8f9fa'
                  }}>
                    <label onClick={e => e.stopPropagation()} style={{ display: 'flex', cursor: 'pointer' }} title="Select all in this category">
                      <input
                        type="checkbox"
                        checked={allInCategorySelected}
                        onChange={() => toggleSelectGroup(items)}
                        style={{ width: '17px', height: '17px', cursor: 'pointer' }}
                      />
                    </label>
                    <button
                      onClick={() => toggleCategory(category)}
                      style={{
                        flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        background: 'none', border: 'none', cursor: 'pointer',
                        fontFamily: 'inherit', fontSize: '15px', fontWeight: '600', color: '#1a1a1a'
                      }}
                    >
                      <span>{category} <span style={{ color: '#888', fontWeight: '400' }}>({items.length})</span></span>
                      <span>{expanded ? '▾' : '▸'}</span>
                    </button>
                  </div>

                  {expanded && (
                    subGroups ? (
                      <div style={{ padding: '16px 20px' }}>
                        {Object.entries(subGroups).map(([subcat, subItems]) => {
                          const subKey = `${category}::${subcat}`;
                          const subExpanded = expandedSubcategories[subKey] !== false; // default open
                          const allSubSelected = subItems.every(i => selectedIds.has(i.id));
                          return (
                            <div key={subKey} style={{ marginBottom: '16px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                <label onClick={e => e.stopPropagation()} style={{ display: 'flex', cursor: 'pointer' }} title="Select all in this subcategory">
                                  <input
                                    type="checkbox"
                                    checked={allSubSelected}
                                    onChange={() => toggleSelectGroup(subItems)}
                                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                  />
                                </label>
                                <button
                                  onClick={() => toggleSubcategory(subKey)}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    fontFamily: 'inherit', fontSize: '13px', fontWeight: '600',
                                    color: subcat === 'Unsorted' ? '#b45309' : '#374151',
                                    textTransform: 'uppercase', letterSpacing: '0.04em'
                                  }}
                                >
                                  <span>{subExpanded ? '▾' : '▸'}</span>
                                  {subcat} ({subItems.length})
                                </button>
                              </div>
                              {subExpanded && (
                                <div className="products-grid" style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                                  gap: '20px'
                                }}>
                                  {subItems.map(product => (
                                    <ProductCard
                                      key={product.id}
                                      product={product}
                                      selected={selectedIds.has(product.id)}
                                      onToggleSelect={toggleSelect}
                                      onEdit={handleEdit}
                                      onDelete={handleDelete}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="products-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                        gap: '20px',
                        padding: '20px'
                      }}>
                        {items.map(product => (
                          <ProductCard
                            key={product.id}
                            product={product}
                            selected={selectedIds.has(product.id)}
                            onToggleSelect={toggleSelect}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                          />
                        ))}
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminNotecards;
